import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, asList, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, FileUp, Loader2, AlertCircle, CheckCircle2, Camera } from "lucide-react";

/**
 * Genereer een korte, unieke client-side key voor een order-regel.
 * Gebruikt voor `key=` prop in React zodat verwijderen/herschikken van rijen
 * geen focus-jumps of state-mix-ups veroorzaakt bij gecontroleerde inputs.
 */
let _lkSeq = 0;
const newLineKey = () => `lk-${++_lkSeq}-${Math.random().toString(36).slice(2, 8)}`;
const withLk = (line) => ({ _lk: newLineKey(), ...line });
const localIsoDate = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 10);
};

/**
 * Visuele indicator van AI scan match-vertrouwen per regel.
 *   high   → groen "Zeker"      (SKU-exact of score >= 18)
 *   medium → blauw "Waarschijnlijk" (score 12-17)
 *   low    → oranje "Onzeker"   (score 10-11, handmatig nakijken)
 *   none   → rood "Geen match"  (regel wacht op handmatige productkeuze)
 */
const CONFIDENCE_META = {
    high:   { label: "Zeker",         cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    medium: { label: "Waarschijnlijk", cls: "bg-sky-100 text-sky-800 border-sky-300" },
    low:    { label: "Onzeker",       cls: "bg-amber-100 text-amber-800 border-amber-300" },
    none:   { label: "Geen match",    cls: "bg-rose-100 text-rose-800 border-rose-300" },
};
function ConfidenceBadge({ confidence, score }) {
    const m = CONFIDENCE_META[confidence] || CONFIDENCE_META.none;
    return (
        <span
            data-testid={`scan-confidence-${confidence}`}
            title={score ? `Match-score: ${score}` : "Geen match"}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold rounded border ${m.cls}`}
        >
            {m.label}
        </span>
    );
}

export default function NewOrder() {
    const { id: editId } = useParams();
    const isEdit = !!editId;
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [locations, setLocations] = useState([]);
    const [customerId, setCustomerId] = useState("");
    const [deliveryDate, setDeliveryDate] = useState(localIsoDate);
    const [reference, setReference] = useState("");
    const [notes, setNotes] = useState("");
    const [stickers, setStickers] = useState(false);
    const [stickersDescription, setStickersDescription] = useState("");
    const [labelType, setLabelType] = useState("own");
    const [quality, setQuality] = useState("A");
    const [cartType, setCartType] = useState("CC");
    const [risers, setRisers] = useState(false);
    const [lines, setLines] = useState([withLk({ product_id: "", quantity: 24, location_id: "" })]);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [scanInfo, setScanInfo] = useState(null);
    const [scanEnabled, setScanEnabled] = useState(false);
    const [scanUnavailableReason, setScanUnavailableReason] = useState("");
    const [isScanReview, setIsScanReview] = useState(false);
    const [scanHeaderReviewed, setScanHeaderReviewed] = useState(false);
    const [loadingOrder, setLoadingOrder] = useState(isEdit);
    const fileRef = useRef();
    const cameraRef = useRef();
    const nav = useNavigate();

    useEffect(() => {
        api.get("/config")
            .then((r) => {
                setScanEnabled(!!r.data.scan_enabled);
                setScanUnavailableReason(r.data.scan_unavailable_reason || "");
            })
            .catch(() => {
                setScanEnabled(false);
                setScanUnavailableReason("De scannerconfiguratie kon niet worden geladen.");
            });
        Promise.all([
            api.get("/customers"),
            api.get("/products"),
            api.get("/locations"),
        ]).then(([c, p, l]) => {
            const customerList = asList(c.data, ["customers"]);
            const productList = asList(p.data, ["products"]);
            const locationList = asList(l.data, ["locations"]);
            setCustomers(customerList);
            setProducts(productList);
            setLocations(locationList);
            if (!isEdit) {
                if (customerList[0]) setCustomerId(customerList[0].id);
                if (productList[0]) {
                    const firstProd = productList[0];
                    const autoLoc =
                        (firstProd.location_ids || []).length === 1
                            ? firstProd.location_ids[0]
                            : "";
                    setLines([withLk({ product_id: firstProd.id, quantity: 24, location_id: autoLoc })]);
                }
            }
        }).catch((e) => {
            setError(formatApiErrorDetail(e.response?.data?.detail ?? "Klant-, product- en locatiegegevens konden niet worden geladen"));
        });
    }, [isEdit]);

    // Laad bestaande bestelling bij bewerken
    useEffect(() => {
        if (!isEdit) return;
        setLoadingOrder(true);
        api.get(`/orders/${editId}`)
            .then((r) => {
                const o = r.data;
                setCustomerId(o.customer_id);
                setDeliveryDate(o.delivery_date || new Date().toISOString().slice(0, 10));
                setReference(o.reference || o.scan_meta?.reference || "");
                setNotes(o.notes || "");
                setStickers(!!o.stickers);
                setStickersDescription(o.stickers_description || "");
                setLabelType(o.label_type || "own");
                setQuality(o.quality || "A");
                setCartType(o.cart_type || "CC");
                setRisers(!!o.risers);
                // Voor scanned orders: merge scan_lines (incl unmatched) als invul-regels.
                // Voor scanned regels zonder match: voeg toe als unmatched form-line zodat user
                // ze handmatig kan koppelen of verwijderen.
                if (o.scan_lines && o.scan_lines.length) {
                    const merged = o.scan_lines.map((sl, scanIndex) => withLk({
                        product_id: sl.product_id || "",
                        quantity: sl.quantity,
                        location_id: "",  // bepaald door geselecteerd product
                        _scanned: true,
                        _hint: [sl.sku, sl.pot_size || sl.code, sl.liters, sl.name, sl.cultivar, sl.description].filter(Boolean).join(" · "),
                        _scan_line_id: sl.scan_line_id || sl.id || `scan-line-${scanIndex + 1}`,
                        _original_product_id: sl.product_id || "",
                        _reviewed: false,
                        _corrected: false,
                        _unmatched: !sl.matched,
                        _confidence: sl.match_confidence || (sl.matched ? "low" : "none"),
                        _score: sl.match_score || 0,
                    }));
                    // Backfill location_id voor matched regels (gebruik order.lines die al location_id hebben)
                    (o.lines || []).forEach((l) => {
                        const target = merged.find((m) => m.product_id === l.product_id && m.location_id === "" && m.quantity === l.quantity);
                        if (target) target.location_id = l.location_id || "";
                    });
                    setIsScanReview(true);
                    setScanHeaderReviewed(false);
                    setLines(merged);
                    if (o.scan_meta?.stats) {
                        const s = o.scan_meta.stats;
                        setScanInfo({
                            total: s.total_lines,
                            matched: s.matched,
                            unmatched: s.unmatched,
                            conf: s.conf || null,
                            extra: [
                                ...(o.scan_meta.customer_name && !o.customer_id
                                    ? [`Klant "${o.scan_meta.customer_name}" niet gevonden — selecteer handmatig`] : []),
                                ...(o.scan_meta.reference ? [`Referentie: ${o.scan_meta.reference}`] : []),
                            ],
                        });
                    }
                } else {
                    const emptyScanDraft = o.scan_status === "scanned";
                    setIsScanReview(emptyScanDraft);
                    setScanHeaderReviewed(false);
                    setLines(
                        (o.lines || []).map((l) => withLk({
                            product_id: l.product_id,
                            quantity: l.quantity,
                            location_id: l.location_id || "",
                        }))
                    );
                    if (emptyScanDraft && o.scan_meta?.stats) {
                        const s = o.scan_meta.stats;
                        setScanInfo({
                            total: s.total_lines,
                            matched: s.matched,
                            unmatched: s.unmatched,
                            conf: s.conf || null,
                            extra: [
                                ...(o.scan_meta.customer_name && !o.customer_id
                                    ? [`Klant "${o.scan_meta.customer_name}" niet gevonden — selecteer handmatig`] : []),
                                ...(o.scan_meta.reference ? [`Referentie: ${o.scan_meta.reference}`] : []),
                            ],
                        });
                    }
                }
            })
            .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail ?? "Kan bestelling niet laden")))
            .finally(() => setLoadingOrder(false));
    }, [isEdit, editId]);

    const addLine = () => {
        const firstProd = products[0];
        const autoLoc =
            firstProd && (firstProd.location_ids || []).length === 1
                ? firstProd.location_ids[0]
                : "";
        setLines([
            ...lines,
            withLk({ product_id: firstProd?.id || "", quantity: 12, location_id: autoLoc }),
        ]);
    };
    const removeLine = (i) => setLines((current) => current.filter((_, idx) => idx !== i));
    const updateLine = (i, k, v) =>
        setLines(
            lines.map((l, idx) => {
                if (idx !== i) return l;
                const updated = { ...l, [k]: v };
                // Auto-select location when product changes & product has exactly 1 location
                if (k === "product_id") {
                    const prod = products.find((p) => p.id === v);
                    const locs = prod?.location_ids || [];
                    if (locs.length === 1) updated.location_id = locs[0];
                    else if (locs.length === 0) updated.location_id = "";
                    else if (!locs.includes(updated.location_id)) updated.location_id = "";
                    if (l._scanned) {
                        updated._unmatched = !v;
                        updated._reviewed = !!v;
                        updated._corrected = !!v && v !== (l._original_product_id || "");
                    }
                }
                return updated;
            })
        );

    const confirmScannedLine = (i) => {
        setLines((current) => current.map((line, idx) => (
            idx === i
                ? {
                    ...line,
                    _reviewed: !!line.product_id,
                    _corrected: !!line.product_id && line.product_id !== (line._original_product_id || ""),
                    _unmatched: !line.product_id,
                }
                : line
        )));
    };

    const onScanFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        if (isEdit) {
            setError("Een bestaande bestelling kan niet met een nieuwe scan worden overschreven.");
            return;
        }
        if (files.length > 1) {
            setError("Upload hier één PDF of foto per bestelling.");
            return;
        }
        setScanning(true);
        setError("");
        setScanInfo(null);
        try {
            const form = new FormData();
            form.append("files", files[0]);
            await api.post("/orders/scan-draft", form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            toast.success("Scan ontvangen. Je kunt de voortgang bij Bestellingen volgen.");
            nav("/bestellingen", { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail ?? "Uploaden mislukt"));
        } finally {
            setScanning(false);
            if (fileRef.current) fileRef.current.value = "";
            if (cameraRef.current) cameraRef.current.value = "";
        }
    };

    const submit = async () => {
        setError("");
        if (isScanReview && !scanHeaderReviewed) {
            return setError("Bevestig eerst dat je de klant en leverdatum hebt gecontroleerd.");
        }
        const pendingReviewIndex = lines.findIndex((line) => line._scanned && !line._reviewed);
        if (isScanReview && pendingReviewIndex >= 0) {
            return setError(`Controleer en bevestig eerst scanregel ${pendingReviewIndex + 1}.`);
        }
        if (!customerId) return setError("Selecteer een klant");
        const invalidLineIndex = lines.findIndex((line) => {
            const productExists = products.some((product) => product.id === line.product_id);
            const quantity = Number(line.quantity);
            return !productExists || !Number.isFinite(quantity) || quantity <= 0;
        });
        if (invalidLineIndex >= 0) {
            const invalidLine = lines[invalidLineIndex];
            const productExists = products.some((product) => product.id === invalidLine.product_id);
            return setError(
                productExists
                    ? `Vul bij regel ${invalidLineIndex + 1} een geldig aantal groter dan 0 in.`
                    : `Kies bij regel ${invalidLineIndex + 1} een geldig product of verwijder de regel.`
            );
        }
        const usable = lines.map((line) => {
            const allowedLocations = products.find((p) => p.id === line.product_id)?.location_ids || [];
            const locationId = allowedLocations.includes(line.location_id)
                ? line.location_id
                : allowedLocations.length === 1
                    ? allowedLocations[0]
                    : "";
            return { ...line, location_id: locationId };
        });
        if (!usable.length) return setError("Voeg minstens één orderregel toe");
        const missingLoc = usable.find((l) => !l.location_id);
        if (missingLoc) {
            const p = products.find((p) => p.id === missingLoc.product_id);
            return setError(`Kies een locatie voor ${p?.sku || "alle regels"} — locatie is verplicht.`);
        }
        setSubmitting(true);
        try {
            const payload = {
                customer_id: customerId,
                delivery_date: deliveryDate,
                reference: reference.trim(),
                notes,
                stickers,
                stickers_description: stickers ? stickersDescription.trim() : "",
                label_type: labelType,
                quality,
                cart_type: cartType,
                risers,
                lines: usable.map((l) => ({
                    product_id: l.product_id,
                    quantity: Number(l.quantity),
                    location_id: l.location_id,
                    ...(l._scan_line_id ? { scan_line_id: l._scan_line_id } : {}),
                })),
                ...(isScanReview ? {
                    scan_feedback: [
                        ...usable
                            .filter((l) => l._scanned && l._scan_line_id)
                            .map((l) => ({
                                scan_line_id: l._scan_line_id,
                                selected_product_id: l.product_id,
                                decision: l.product_id === (l._original_product_id || "")
                                    ? "confirmed"
                                    : "corrected",
                            })),
                    ],
                } : {}),
            };
            let order;
            try {
                const r = isEdit
                    ? await api.put(`/orders/${editId}`, payload)
                    : await api.post("/orders", payload);
                order = r.data;
            } catch (e) {
                setError(formatApiErrorDetail(e.response?.data?.detail ?? (isEdit ? "Bijwerken mislukt" : "Aanmaken mislukt")));
                return;
            }

            try {
                const calc = await api.post(`/orders/${order.id}/calculate`);
                nav(`/berekening/${calc.data.id}`);
            } catch (e) {
                // De bestelling bestaat al: voorkom dat opnieuw klikken een
                // duplicaat maakt en stuur naar het opgeslagen exemplaar.
                toast.error("Bestelling is opgeslagen, maar berekenen lukte niet. Je kunt het vanuit de bestelling opnieuw proberen.");
                nav(`/bestellingen/${order.id}`, { replace: true });
            }
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail ?? "Er ging onverwacht iets mis"));
        } finally {
            setSubmitting(false);
        }
    };

    const productMap = Object.fromEntries(products.map((p) => [p.id, p]));
    const pendingScanReviews = lines.filter((line) => line._scanned && !line._reviewed).length;
    const scanReviewBlocked = isScanReview && (!scanHeaderReviewed || pendingScanReviews > 0);

    if (loadingOrder) {
        return <div className="p-12 text-ink-500">Bestelling laden…</div>;
    }

    return (
        <div className="p-5 sm:p-7 lg:p-10 xl:p-12 max-w-[1280px] mx-auto pb-24 lg:pb-12">
            <PageHeader
                kicker={isEdit ? "Bestelling bewerken" : "Werkstroom · stap 1"}
                title={isEdit ? "Bestelling aanpassen" : "Nieuwe bestelling"}
                subtitle={
                    isEdit
                        ? "Pas regels of opties aan. Status gaat terug naar 'open' en de schuur ziet een 'bewerkt'-label zodat opnieuw geprint moet worden."
                        : scanEnabled
                            ? "Vul handmatig in of upload één raaplijst (PDF / foto). De scan verschijnt bij Bestellingen om te controleren."
                            : scanUnavailableReason || "Vul de bestelling handmatig in. De AI-scanner is nog niet geactiveerd."
                }
                testid="new-order-header"
                actions={
                    !isEdit ? <>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.heic,.heif,.webp"
                            className="hidden"
                            onChange={onScanFiles}
                            data-testid="input-scan-files"
                        />
                        <input
                            ref={cameraRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={onScanFiles}
                            data-testid="input-scan-camera"
                        />
                        <button
                            onClick={() => cameraRef.current?.click()}
                            disabled={scanning || !scanEnabled}
                            data-testid="btn-scan-camera"
                            className="lg:hidden px-3 py-2 rounded-sm border border-florefy-900 bg-florefy-100 hover:bg-florefy-700 hover:text-white text-florefy-700 text-sm flex items-center gap-2 disabled:opacity-50 transition-colors"
                            title={scanEnabled ? "Foto maken met camera" : scanUnavailableReason || "AI-scannen is niet actief"}
                        >
                            <Camera className="h-4 w-4" /> Foto maken
                        </button>
                        <button
                            onClick={() => fileRef.current?.click()}
                            disabled={scanning || !scanEnabled}
                            title={scanEnabled ? "Raaplijst automatisch inlezen" : scanUnavailableReason || "AI-scannen is niet actief"}
                            data-testid="btn-scan-pdf"
                            className="px-3 lg:px-4 py-2 rounded-sm border border-florefy-900 bg-florefy-100 hover:bg-florefy-700 hover:text-white text-florefy-700 text-sm flex items-center gap-2 disabled:opacity-50 transition-colors"
                        >
                            {scanning ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" /> AI leest in…
                                </>
                            ) : (
                                <>
                                    <FileUp className="h-4 w-4" />
                                    {scanEnabled ? "Eén raaplijst uploaden" : "AI-scan niet actief"}
                                </>
                            )}
                        </button>
                    </> : null
                }
            />

            {error && (
                <div className="bg-destructive/10 border border-destructive text-destructive text-sm p-3 mb-4 rounded-sm flex gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /> {error}
                </div>
            )}
            {scanInfo && (
                <div
                    className="bg-florefy-100 border border-ink-200 text-ink-700 text-sm p-3 mb-4 rounded-sm flex gap-2"
                    data-testid="scan-info"
                >
                    <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-ink-500" />
                    <div className="flex-1">
                        <div className="font-medium">
                            {scanInfo.total} regels ingelezen · {scanInfo.matched} gekoppeld
                            {scanInfo.unmatched > 0 && ` · ${scanInfo.unmatched} onbekend (kies product)`}
                        </div>
                        {scanInfo.conf && (
                            <div className="flex flex-wrap gap-2 mt-2" data-testid="scan-confidence-breakdown">
                                {scanInfo.conf.high > 0 && (
                                    <ConfidenceBadge confidence="high" score={0} />
                                )}
                                {scanInfo.conf.high > 0 && <span className="text-xs text-ink-600">{scanInfo.conf.high} zeker</span>}
                                {scanInfo.conf.medium > 0 && (
                                    <ConfidenceBadge confidence="medium" score={0} />
                                )}
                                {scanInfo.conf.medium > 0 && <span className="text-xs text-ink-600">{scanInfo.conf.medium} waarschijnlijk</span>}
                                {scanInfo.conf.low > 0 && (
                                    <ConfidenceBadge confidence="low" score={0} />
                                )}
                                {scanInfo.conf.low > 0 && <span className="text-xs text-ink-600">{scanInfo.conf.low} onzeker — controleer</span>}
                                {scanInfo.conf.none > 0 && (
                                    <ConfidenceBadge confidence="none" score={0} />
                                )}
                                {scanInfo.conf.none > 0 && <span className="text-xs text-ink-600">{scanInfo.conf.none} geen match</span>}
                            </div>
                        )}
                        {scanInfo.extra.map((e, i) => (
                            <div key={`extra-${i}-${e.slice(0, 20)}`} className="text-xs mt-0.5">{e}</div>
                        ))}
                    </div>
                </div>
            )}

            {isScanReview && (
                <div
                    className="bg-sky-50 border border-sky-300 text-sky-950 p-4 mb-5 rounded-md"
                    data-testid="scan-review-checklist"
                >
                    <div className="font-semibold text-sm">Controleer de scan voordat je de bestelling verwerkt</div>
                    <p className="text-xs text-sky-800 mt-1">
                        Bevestig iedere productregel hieronder. Een aangepaste productkeuze wordt als correctie voor deze kwekerij opgeslagen.
                    </p>
                    <label className="mt-3 flex items-start gap-2.5 cursor-pointer text-sm font-medium">
                        <input
                            type="checkbox"
                            checked={scanHeaderReviewed}
                            onChange={(e) => setScanHeaderReviewed(e.target.checked)}
                            className="mt-0.5 h-4 w-4 accent-florefy-900"
                            data-testid="scan-header-reviewed"
                        />
                        <span>Ik heb de klant en leverdatum gecontroleerd.</span>
                    </label>
                    {pendingScanReviews > 0 && (
                        <div className="text-xs font-medium text-amber-800 mt-2" data-testid="scan-review-pending-count">
                            Nog {pendingScanReviews} {pendingScanReviews === 1 ? "scanregel" : "scanregels"} te bevestigen.
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white border border-border rounded-sm p-5 space-y-4 lg:col-span-1">
                    <h2 className="text-base font-semibold text-ink-900">Order gegevens</h2>
                    <div>
                        <div className="data-label mb-1">Klant</div>
                        <select
                            className={inputCls}
                            value={customerId}
                            onChange={(e) => setCustomerId(e.target.value)}
                            data-testid="select-customer"
                        >
                            <option value="">— kies klant —</option>
                            {customers.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <div className="data-label mb-1">Leverdatum</div>
                        <input
                            type="date"
                            className={inputCls}
                            value={deliveryDate}
                            onChange={(e) => setDeliveryDate(e.target.value)}
                            data-testid="input-delivery-date"
                        />
                    </div>
                    <div>
                        <div className="data-label mb-1">Orderreferentie</div>
                        <input
                            type="text"
                            maxLength={200}
                            className={inputCls}
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="Bijvoorbeeld inkoop- of ordernummer"
                            data-testid="input-order-reference"
                        />
                    </div>
                    <div>
                        <div className="data-label mb-1">Notities</div>
                        <textarea
                            className={inputCls}
                            rows="3"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="input-notes"
                        />
                    </div>
                    <div className="pt-4 border-t border-border space-y-4" data-testid="order-options">
                        <div className="data-label text-ink-700 font-semibold">Opmerkingen voor de hele order</div>

                        <div>
                            <div className="data-label mb-1">Type kar</div>
                            <div className="grid grid-cols-2 gap-2" data-testid="cart-type-group">
                                {["CC", "DC"].map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setCartType(t)}
                                        data-testid={`cart-type-${t}`}
                                        data-selected={cartType === t}
                                        className={`px-3 py-2 rounded-sm text-sm font-medium border transition-colors ${
                                            cartType === t
                                                ? "bg-florefy-900 text-white border-florefy-900"
                                                : "bg-white text-ink-700 border-border hover:bg-ink-50"
                                        }`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                            <div className="text-[11px] text-ink-500 mt-1">Alleen aanduiding — telt niet mee in de berekening.</div>
                        </div>

                        <div>
                            <div className="data-label mb-1">Kwaliteit</div>
                            <div className="grid grid-cols-3 gap-2" data-testid="quality-group">
                                {["A+", "A", "B"].map((q) => (
                                    <button
                                        key={q}
                                        type="button"
                                        onClick={() => setQuality(q)}
                                        data-testid={`quality-${q}`}
                                        data-selected={quality === q}
                                        className={`px-3 py-2 rounded-sm text-sm font-medium border transition-colors ${
                                            quality === q
                                                ? "bg-florefy-900 text-white border-florefy-900"
                                                : "bg-white text-ink-700 border-border hover:bg-ink-50"
                                        }`}
                                    >
                                        {q}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="data-label mb-1">Etiket</div>
                            <div className="grid grid-cols-3 gap-2" data-testid="label-type-group">
                                {[
                                    { v: "own", l: "Ons eigen" },
                                    { v: "customer", l: "Klant eigen" },
                                    { v: "none", l: "Geen etiket" },
                                ].map((opt) => (
                                    <button
                                        key={opt.v}
                                        type="button"
                                        onClick={() => setLabelType(opt.v)}
                                        data-testid={`label-type-${opt.v}`}
                                        data-selected={labelType === opt.v}
                                        className={`px-3 py-2 rounded-sm text-sm font-medium border transition-colors ${
                                            labelType === opt.v
                                                ? "bg-florefy-900 text-white border-florefy-900"
                                                : "bg-white text-ink-700 border-border hover:bg-ink-50"
                                        }`}
                                    >
                                        {opt.l}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="data-label mb-1">Stickers</div>
                            <div className="grid grid-cols-2 gap-2" data-testid="stickers-group">
                                <button
                                    type="button"
                                    onClick={() => setStickers(false)}
                                    data-testid="stickers-no"
                                    data-selected={!stickers}
                                    className={`px-3 py-2 rounded-sm text-sm font-medium border transition-colors ${
                                        !stickers
                                            ? "bg-florefy-900 text-white border-florefy-900"
                                            : "bg-white text-ink-700 border-border hover:bg-ink-50"
                                    }`}
                                >
                                    Nee
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStickers(true)}
                                    data-testid="stickers-yes"
                                    data-selected={stickers}
                                    className={`px-3 py-2 rounded-sm text-sm font-medium border transition-colors ${
                                        stickers
                                            ? "bg-florefy-900 text-white border-florefy-900"
                                            : "bg-white text-ink-700 border-border hover:bg-ink-50"
                                    }`}
                                >
                                    Ja
                                </button>
                            </div>
                            {stickers && (
                                <input
                                    type="text"
                                    placeholder="Sticker-omschrijving (bv. code BIO-2026)"
                                    className={`${inputCls} mt-2`}
                                    value={stickersDescription}
                                    onChange={(e) => setStickersDescription(e.target.value)}
                                    data-testid="input-stickers-description"
                                />
                            )}
                        </div>

                        <div>
                            <div className="data-label mb-1">Opzetstukken</div>
                            <div className="grid grid-cols-2 gap-2" data-testid="risers-group">
                                <button
                                    type="button"
                                    onClick={() => setRisers(false)}
                                    data-testid="risers-no"
                                    data-selected={!risers}
                                    className={`px-3 py-2 rounded-sm text-sm font-medium border transition-colors ${
                                        !risers
                                            ? "bg-florefy-900 text-white border-florefy-900"
                                            : "bg-white text-ink-700 border-border hover:bg-ink-50"
                                    }`}
                                >
                                    Nee
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRisers(true)}
                                    data-testid="risers-yes"
                                    data-selected={risers}
                                    className={`px-3 py-2 rounded-sm text-sm font-medium border transition-colors ${
                                        risers
                                            ? "bg-florefy-900 text-white border-florefy-900"
                                            : "bg-white text-ink-700 border-border hover:bg-ink-50"
                                    }`}
                                >
                                    Ja (+7 posities)
                                </button>
                            </div>
                            <div className="text-[11px] text-ink-500 mt-1">
                                {risers
                                    ? "Berekening met 42 posities per paal (35 + 7 opzetstukken)."
                                    : "Berekening met standaard 35 posities per paal."}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white border border-border rounded-sm p-5 lg:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-base font-semibold text-ink-900">Orderregels</h2>
                        <button
                            onClick={addLine}
                            className="text-sm flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm hover:bg-ink-50"
                            data-testid="btn-add-line"
                        >
                            <Plus className="h-3.5 w-3.5" /> Regel toevoegen
                        </button>
                    </div>
                    <div className="space-y-2">
                        {lines.map((l, i) => {
                            const p = productMap[l.product_id];
                            const layers = p ? Math.ceil(Number(l.quantity || 0) / p.units_per_layer) : 0;
                            return (
                                <div
                                    key={l._lk || `idx-${i}`}
                                    className={`pb-3 border-b border-border/40 ${
                                        l._reviewed && l._corrected
                                            ? "bg-sky-50 -mx-2 px-2 rounded-sm"
                                            : l._reviewed
                                            ? "bg-emerald-50 -mx-2 px-2 rounded-sm"
                                            : l._unmatched
                                            ? "bg-rose-50 -mx-2 px-2 rounded-sm"
                                            : l._confidence === "low"
                                            ? "bg-amber-50 -mx-2 px-2 rounded-sm"
                                            : ""
                                    }`}
                                    data-testid={`order-line-${i}`}
                                >
                                    {l._scanned && (
                                        <div className="text-[11px] font-mono text-ink-500 mb-1 flex items-center flex-wrap gap-1">
                                            {l._reviewed ? (
                                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-semibold uppercase tracking-wide ${
                                                    l._corrected
                                                        ? "bg-sky-100 text-sky-800 border-sky-300"
                                                        : "bg-emerald-100 text-emerald-800 border-emerald-300"
                                                }`}>
                                                    <CheckCircle2 className="h-3 w-3" />
                                                    {l._corrected ? "Gecorrigeerd" : "Gecontroleerd"}
                                                </span>
                                            ) : (
                                                <ConfidenceBadge confidence={l._confidence} score={l._score} />
                                            )}
                                            <span>uit raaplijst:</span>
                                            <span className="italic">{l._hint || "geen brontekst herkend"}</span>
                                            {!l._reviewed && l._unmatched && (
                                                <span className="text-rose-700 font-medium ml-1">— kies product</span>
                                            )}
                                            {!l._reviewed && l._confidence === "low" && !l._unmatched && (
                                                <span className="text-amber-800 font-medium ml-1">— controleer match</span>
                                            )}
                                        </div>
                                    )}
                                    <div className="grid grid-cols-12 gap-3 items-end">
                                        <div className="col-span-5">
                                            <div className="data-label mb-1">Product</div>
                                            <select
                                                className={`${inputCls} ${
                                                    l._unmatched
                                                        ? "border-rose-500"
                                                        : l._confidence === "low"
                                                        ? "border-amber-500"
                                                        : ""
                                                }`}
                                                value={l.product_id}
                                                onChange={(e) => updateLine(i, "product_id", e.target.value)}
                                                data-testid={`select-product-${i}`}
                                            >
                                                <option value="">— kies product —</option>
                                                {products.map((p) => (
                                                    <option key={p.id} value={p.id}>
                                                        {p.sku} — {p.name}
                                                        {p.cultivar ? ` '${p.cultivar}'` : ""}
                                                        {" "}({p.pot_size})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-span-3">
                                            <div className="data-label mb-1">Locatie *</div>
                                            {(() => {
                                                const prod = productMap[l.product_id];
                                                const allowed = prod?.location_ids || [];
                                                const opts = locations.filter((loc) => allowed.includes(loc.id));
                                                if (!prod) {
                                                    return (
                                                        <select className={inputCls} disabled data-testid={`select-location-${i}`}>
                                                            <option>—</option>
                                                        </select>
                                                    );
                                                }
                                                if (opts.length === 0) {
                                                    return (
                                                        <div className="text-[11px] text-yellow-800 bg-yellow-50 border border-yellow-300 px-2 py-1.5 rounded-sm" data-testid={`location-warn-${i}`}>
                                                            ⚠ Geen locatie aan dit product gekoppeld — open <em>Beheer → Producten</em>.
                                                        </div>
                                                    );
                                                }
                                                if (opts.length === 1) {
                                                    return (
                                                        <div
                                                            className="px-3 py-2 border border-border rounded-sm bg-ink-50 text-xs font-mono text-ink-700"
                                                            data-testid={`location-fixed-${i}`}
                                                            data-location-id={opts[0].id}
                                                        >
                                                            {opts[0].code} · {opts[0].name}
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <select
                                                        className={`${inputCls} ${!l.location_id ? "border-yellow-500" : ""}`}
                                                        value={l.location_id || ""}
                                                        onChange={(e) => updateLine(i, "location_id", e.target.value)}
                                                        data-testid={`select-location-${i}`}
                                                    >
                                                        <option value="">— kies locatie —</option>
                                                        {opts.map((loc) => (
                                                            <option key={loc.id} value={loc.id}>
                                                                {loc.code} · {loc.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                );
                                            })()}
                                        </div>
                                        <div className="col-span-2">
                                            <div className="data-label mb-1">Aantal</div>
                                            <input
                                                type="number"
                                                min="1"
                                                className={inputCls}
                                                value={l.quantity}
                                                onChange={(e) => updateLine(i, "quantity", e.target.value)}
                                                data-testid={`input-qty-${i}`}
                                            />
                                        </div>
                                        <div className="col-span-1 text-center">
                                            <div className="data-label mb-1">Lagen</div>
                                            <div className="font-mono text-base text-ink-900">{layers || "—"}</div>
                                        </div>
                                        <div className="col-span-1 text-right">
                                            <button
                                                onClick={() => removeLine(i)}
                                                className="text-ink-700 hover:text-destructive p-2"
                                                disabled={lines.length === 1}
                                                data-testid={`btn-remove-line-${i}`}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                    {l._scanned && (
                                        <div className="flex justify-end mt-2">
                                            <button
                                                type="button"
                                                onClick={() => confirmScannedLine(i)}
                                                disabled={!l.product_id || l._reviewed}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-semibold transition-colors ${
                                                    l._reviewed
                                                        ? "bg-emerald-100 border-emerald-300 text-emerald-800"
                                                        : l.product_id
                                                        ? "bg-white border-florefy-700 text-florefy-900 hover:bg-florefy-50"
                                                        : "bg-ink-50 border-border text-ink-400"
                                                } disabled:cursor-default`}
                                                data-testid={`btn-review-scan-line-${i}`}
                                            >
                                                <CheckCircle2 className="h-3.5 w-3.5" />
                                                {l._reviewed
                                                    ? l._corrected ? "Correctie gecontroleerd" : "Gecontroleerd"
                                                    : l.product_id ? "Bevestig product" : "Kies eerst een product"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                        {scanReviewBlocked && (
                            <div className="mr-auto text-xs text-amber-800 self-center" data-testid="scan-review-submit-help">
                                Rond eerst de scancontrole af.
                            </div>
                        )}
                        <button
                            onClick={submit}
                            disabled={submitting || scanReviewBlocked}
                            data-testid="btn-calculate"
                            className="px-6 py-2.5 rounded-sm bg-florefy-900 hover:bg-florefy-700 text-white text-sm font-medium disabled:opacity-50"
                        >
                            {submitting ? "Bezig…" : isEdit ? "Opslaan & opnieuw berekenen →" : "Bereken karren →"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const inputCls =
    "w-full px-3 py-2 border border-border rounded-sm bg-white font-mono text-sm focus:outline-none focus:ring-1 focus:ring-florefy-900";
